import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { signInInternal } from '../../features/auth/authSlice'
import { ACCESS_TOKEN, REDIRECT_AFTER_LOGIN, REFRESH_TOKEN, StorageSetItem, USER } from "../../lib/storage"
import { sameOriginPath } from "../../lib/safeRedirect"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import { setSentryUser } from "../../lib/sentryUser"
import { noticeFor } from "../../lib/signInNotices"

const InternalSignIn = () => {
  const dispatch = useDispatch()
  const [reqError, setReqError] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const notice = noticeFor(searchParams.get('notice'))
    if (!notice) return

    showSnackBar(notice.severity, notice.text)
    // Through the router, not history.replaceState, so react-router's location stays authoritative
    // and a reload does not repeat the message. See noticeFor: a key travels, never text.
    const rest = new URLSearchParams(searchParams)
    rest.delete('notice')
    setSearchParams(rest, { replace: true })
  }, [searchParams, setSearchParams])

  const handleSignIn = async () => {
    const credentials = { email, password }
    const response = await dispatch(signInInternal(credentials))

    if (signInInternal.fulfilled.match(response)) {
      const data = response.payload
      await saveTokens(data.data)
    } else if (response.error) {
      setReqError(response.payload)
    } else if (signInInternal.rejected.match(response)) {
      const error = response.error
      console.error(`Sign-in failed: ${error}`)
    }
  }

  useEffect(() => {
    const handleReqError = () => {
      reqError.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }

    handleReqError()
  }, [reqError])

  const saveTokens = async ({ accessToken, refreshToken, user }) => {
    await StorageSetItem(ACCESS_TOKEN, accessToken)
    await StorageSetItem(REFRESH_TOKEN, refreshToken)
    await StorageSetItem(USER, user)
    setSentryUser(user)

    const redirectUrl = sameOriginPath(localStorage.getItem(REDIRECT_AFTER_LOGIN))
    localStorage.removeItem(REDIRECT_AFTER_LOGIN)
    if (redirectUrl) {
      window.location.href = redirectUrl
    } else {
      window.location.href = '/'
    }
  }

  useEffect(() => {
    const link = document.createElement('link')
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=DM+Sans:wght@300;400;500&display=swap'
    link.rel = 'stylesheet'
    document.head.appendChild(link)
    return () => document.head.removeChild(link)
  }, [])

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --bg: #0f0f0f;
          --panel: #161616;
          --border: rgba(255,255,255,0.07);
          --text: #f0ede8;
          --muted: rgba(240,237,232,0.45);
          --accent: #6b63f5;
          --accent-hover: #7c75f7;
          --accent-glow: rgba(107,99,245,0.25);
          --input-bg: rgba(255,255,255,0.05);
          --input-border: rgba(255,255,255,0.1);
          --input-focus: rgba(107,99,245,0.5);
        }

        .mdl-body {
          font-family: 'DM Sans', sans-serif;
          background: var(--bg);
          color: var(--text);
          min-height: 100vh;
          display: flex;
          overflow: hidden;
        }

        .mdl-left {
          flex: 1;
          position: relative;
          overflow: hidden;
        }

        .mdl-left-bg {
          width: 100%; height: 100%;
          background: linear-gradient(160deg, #0a0a0a 0%, #1a1a2e 50%, #0f0f0f 100%);
          position: absolute; inset: 0;
        }

        .mdl-left svg.mdl-grid {
          position: absolute; inset: 0;
          width: 100%; height: 100%;
          opacity: 0.06;
        }

        .mdl-left svg.mdl-accent-line {
          position: absolute; inset: 0;
          width: 100%; height: 100%;
          opacity: 0.15;
        }

        .mdl-left-text {
          position: absolute;
          bottom: 56px; left: 52px; right: 52px;
        }

        .mdl-left-text .mdl-tag {
          display: inline-block;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--accent);
          border: 1px solid rgba(107,99,245,0.4);
          padding: 5px 12px;
          border-radius: 100px;
          margin-bottom: 20px;
        }

        .mdl-left-text h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(32px, 3.5vw, 52px);
          font-weight: 300;
          line-height: 1.15;
          color: var(--text);
          margin-bottom: 16px;
        }

        .mdl-left-text p {
          font-size: 13px;
          font-weight: 300;
          color: var(--muted);
          line-height: 1.7;
          max-width: 340px;
        }

        .mdl-right {
          width: 480px;
          min-width: 420px;
          background: var(--panel);
          display: flex;
          flex-direction: column;
          padding: 0 52px 52px;
          position: relative;
          border-left: 1px solid var(--border);
        }

        .mdl-right::before {
          content: '';
          position: absolute;
          top: -120px; right: -120px;
          width: 400px; height: 400px;
          border-radius: 50%;
          background: radial-gradient(circle, var(--accent-glow) 0%, transparent 70%);
          pointer-events: none;
        }

        .mdl-logo-area {
          padding: 40px 0 0;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .mdl-logo-text {
          display: flex;
          flex-direction: column;
          line-height: 1;
        }

        .mdl-logo-text span:first-child {
          font-family: 'Cormorant Garamond', serif;
          font-size: 16px;
          font-weight: 500;
          letter-spacing: 0.05em;
          color: var(--text);
        }

        .mdl-logo-text span:last-child {
          font-size: 9px;
          font-weight: 400;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--muted);
          margin-top: 2px;
        }

        .mdl-portal-badge {
          position: absolute;
          top: 40px; right: 52px;
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 10px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--muted);
        }

        .mdl-badge-dot {
          width: 6px; height: 6px;
          border-radius: 50%;
          background: var(--accent);
          box-shadow: 0 0 6px var(--accent);
          animation: mdl-pulse 2s infinite;
        }

        @keyframes mdl-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }

        .mdl-form-area {
          flex: 1;
          display: flex;
          flex-direction: column;
          animation: mdl-fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1);
        }

        @keyframes mdl-fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .mdl-form-header {
          margin-top: 56px;
          margin-bottom: 36px;
        }

        .mdl-form-header .mdl-portal-label {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--accent);
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .mdl-form-header .mdl-portal-label::before {
          content: '';
          display: inline-block;
          width: 18px; height: 1px;
          background: currentColor;
        }

        .mdl-form-header h1 {
          font-family: 'Cormorant Garamond', serif;
          font-size: 38px;
          font-weight: 400;
          letter-spacing: -0.02em;
          line-height: 1.1;
          color: var(--text);
        }

        .mdl-form-header p {
          margin-top: 10px;
          font-size: 13px;
          font-weight: 300;
          color: var(--muted);
          line-height: 1.6;
        }

        .mdl-field { margin-bottom: 18px; }

        .mdl-field label {
          display: block;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--muted);
          margin-bottom: 8px;
        }

        .mdl-input-wrap { position: relative; }

        .mdl-input {
          width: 100%;
          padding: 14px 18px;
          background: var(--input-bg);
          border: 1px solid var(--input-border);
          border-radius: 10px;
          color: var(--text);
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 300;
          outline: none;
          transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
          -webkit-appearance: none;
        }

        .mdl-input::placeholder { color: rgba(240,237,232,0.2); }

        .mdl-input:focus {
          border-color: var(--input-focus);
          background: rgba(107,99,245,0.05);
          box-shadow: 0 0 0 3px rgba(107,99,245,0.1);
        }

        .mdl-input-wrap .mdl-input {
          padding-right: 48px;
        }

        .mdl-eye-btn {
          position: absolute;
          right: 14px; top: 50%;
          transform: translateY(-50%);
          background: none; border: none;
          color: var(--muted);
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          transition: color 0.2s;
        }

        .mdl-eye-btn:hover { color: var(--text); }

        .mdl-forgot {
          text-align: right;
          margin-top: -10px;
          margin-bottom: 28px;
        }

        .mdl-forgot a {
          font-size: 12px;
          color: var(--muted);
          text-decoration: none;
          transition: color 0.2s;
          letter-spacing: 0.02em;
        }

        .mdl-forgot a:hover { color: var(--text); }

        .mdl-btn-login {
          width: 100%;
          padding: 15px;
          border: none;
          border-radius: 10px;
          background: var(--accent);
          color: #fff;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 500;
          letter-spacing: 0.06em;
          cursor: pointer;
          transition: background 0.3s, box-shadow 0.3s, transform 0.15s;
        }

        .mdl-btn-login:hover {
          background: var(--accent-hover);
          box-shadow: 0 8px 24px rgba(107,99,245,0.35);
          transform: translateY(-1px);
        }

        .mdl-btn-login:active { transform: translateY(0); }

        .mdl-divider {
          display: flex;
          align-items: center;
          gap: 14px;
          margin: 28px 0;
          color: var(--muted);
          font-size: 11px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .mdl-divider::before, .mdl-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--border);
        }

        .mdl-help-text {
          text-align: center;
          font-size: 12px;
          color: var(--muted);
          line-height: 1.6;
        }

        .mdl-help-text a {
          color: var(--accent);
          text-decoration: none;
          font-weight: 500;
        }

        .mdl-help-text a:hover { opacity: 0.8; }

        @media (max-width: 800px) {
          .mdl-left { display: none; }
          .mdl-right { width: 100%; min-width: unset; }
        }
      `}</style>

      <div className="mdl-body">
        <div className="mdl-left">
          <div className="mdl-left-bg" />
          <svg className="mdl-grid" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="mdl-grid-pat" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#mdl-grid-pat)" />
          </svg>
          <svg className="mdl-accent-line" xmlns="http://www.w3.org/2000/svg">
            <line x1="0" y1="100%" x2="100%" y2="0" stroke="#6b63f5" strokeWidth="1" />
            <line x1="-5%" y1="105%" x2="95%" y2="-5%" stroke="#6b63f5" strokeWidth="0.5" />
          </svg>
          <div className="mdl-left-text">
            <span className="mdl-tag">Staff Portal</span>
            <h2>Manage cases,<br />protect clients.</h2>
            <p>Access your case management dashboard, client files, and internal tools — all in one secure workspace.</p>
          </div>
        </div>

        <div className="mdl-right">
          <div className="mdl-logo-area">
            <img src={require('../../assets/images/logo.png')} alt="MDL" style={{ width: 36, height: 36, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
            <div className="mdl-logo-text">
              <span>My Debt Lawyers</span>
              <span>Legal Services</span>
            </div>
          </div>

          <div className="mdl-portal-badge">
            <span className="mdl-badge-dot" />
            <span>Staff Portal</span>
          </div>

          <div className="mdl-form-area">
            <div className="mdl-form-header">
              <div className="mdl-portal-label">Internal Access</div>
              <h1>Welcome back,<br />team.</h1>
              <p>Sign in with your staff credentials to access the CRM.</p>
            </div>

            <div className="mdl-field">
              <label>Email address</label>
              <input
                className="mdl-input"
                type="email"
                placeholder="you@mydebtlawyers.com"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="mdl-field">
              <label>Password</label>
              <div className="mdl-input-wrap">
                <input
                  className="mdl-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="mdl-eye-btn"
                  tabIndex={-1}
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="mdl-forgot">
              <a href="/forgot-password/internal">Forgot password?</a>
            </div>

            <button className="mdl-btn-login" onClick={handleSignIn}>
              Sign In to Dashboard
            </button>

          </div>
        </div>
      </div>
    </>
  )
}

export default InternalSignIn
